import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { VORGANG_PROZESS_SCHRITT_LABEL } from '@/constants/vorgangProzessSchritt';
import { VorgangProzessSchritt } from '@/features/schemaValidation/stepValidationSchemas';
import { cn } from '@/lib/utils';
import type { VorgangListeItem } from '@/types';

interface SchrittGruppe {
  schritt: VorgangProzessSchritt;
  count: number;
}

/**
 * Nur bei diesen Schritten ist eine Massenbearbeitung des JSON sinnvoll,
 * dies wurde fachlich abgeklärt.
 */
const SCHRITT_REIHENFOLGE: VorgangProzessSchritt[] = [
  VorgangProzessSchritt.TRANSFORMATION,
  VorgangProzessSchritt.FACHLICHE_VALIDIERUNG,
  VorgangProzessSchritt.WEITERLEITUNG_AN_DOKUMENTDIENSTLEISTER,
];

function gruppiereNachSchritt(
  vorgaenge: readonly VorgangListeItem[],
): SchrittGruppe[] {
  const counts = new Map<VorgangProzessSchritt, number>();
  for (const vorgang of vorgaenge) {
    if (!vorgang.currentStep) continue;
    counts.set(vorgang.currentStep, (counts.get(vorgang.currentStep) ?? 0) + 1);
  }
  return SCHRITT_REIHENFOLGE.flatMap((schritt) => {
    const count = counts.get(schritt);
    return count ? [{ schritt, count }] : [];
  });
}

interface AbarbeitungsSchrittAuswahlProps {
  vorgaenge: readonly VorgangListeItem[];
  aktiverSchritt: VorgangProzessSchritt | null;
  onSchrittAktivieren: (schritt: VorgangProzessSchritt | null) => void;
}

export const AbarbeitungsSchrittAuswahl: React.FC<
  AbarbeitungsSchrittAuswahlProps
> = ({ vorgaenge, aktiverSchritt, onSchrittAktivieren }) => {
  const gruppen = useMemo(() => gruppiereNachSchritt(vorgaenge), [vorgaenge]);

  if (gruppen.length === 0) {
    return (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Fehler pro Schritt</h2>
        <p className="text-sm text-text-secondary">
          Keine Vorgänge im Fehler-Status.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">Fehler pro Schritt</h2>

      <div className="flex flex-wrap gap-3">
        {gruppen.map(({ schritt, count }) => {
          const istAktiv = aktiverSchritt === schritt;
          return (
            <button
              key={schritt}
              type="button"
              onClick={() => onSchrittAktivieren(istAktiv ? null : schritt)}
              aria-pressed={istAktiv}
              className={cn(
                'relative rounde' +
                  'd-md border bg-background px-4 py-3 text-sm text-text-primary transition-colors hover:bg-accent',
                istAktiv && 'border-primary bg-accent',
              )}
            >
              {VORGANG_PROZESS_SCHRITT_LABEL[schritt]}
              <Badge variant="destructive" className="absolute -right-2 -top-2">
                {count}
              </Badge>
            </button>
          );
        })}
      </div>
    </section>
  );
};
